import React from "react";
import { Link, useParams } from "react-router-dom";
import { FloatingWhatsApp } from "react-floating-whatsapp";
import { findMenuItem, findCategory, photoAlt } from "../data/menu";
import { articles } from "../data/articles";
import { faqs } from "../data/faqs";
import { business } from "../data/business";
import { useTheme, useDocumentTitle, useSite } from "../hooks/useSite";
import FaqList from "../components/FaqList";
import ArticleCard from "../components/ArticleCard";
import { VegMark } from "../components/MenuItemCard";
import CategoryIcon from "../illustrations/CategoryIcon";
import NotFoundPage from "./NotFoundPage";
import waAvatar from "../../components/Socialmedia/wtsaplogo2.jpeg";

// Item detail: the dish-detail template adapted for photographed menu items.
export default function MenuItemPage() {
  const { id } = useParams();
  const item = findMenuItem(id);
  const category = item ? findCategory(item.categorySlug) : null;
  const { openModal } = useSite();
  useTheme(item);
  useDocumentTitle(item ? item.name : "Item not found");
  if (!item) return <NotFoundPage />;

  const words = item.name.split(" ");
  const head = words.length > 1 ? words.slice(0, -1).join(" ") : item.name;
  const tail = words.length > 1 ? words[words.length - 1] : "";
  const related = articles.filter((a) => a.category === (item.veg && /roll|sundae|cone|shake|plate|signature/i.test(item.category) ? "Sweet" : "Savoury")).slice(0, 2);
  const siblings = category.items;

  return (
    <article key={item.id}>
      <section className="ro-detail-top ro-detail-top--item">
        <div className="ro-container">
          <p className="ro-item-crumb">
            <Link to="/menu">Our Menu</Link> / <Link to={`/menu/c/${category.slug}`}>{category.name}</Link>
          </p>
          <h1>
            <strong>{head}</strong> {tail && <span className="ro-outline">{tail}</span>}
          </h1>
          <div className="ro-detail-main ro-detail-main--item">
            <div className="ro-item-hero">
              {item.photo ? (
                <img src={item.photo} alt={photoAlt(item)} />
              ) : (
                <span className="ro-item-card__placeholder">
                  <CategoryIcon name={category.icon} />
                </span>
              )}
            </div>
            <button type="button" className="ro-pill ro-detail-main__order" onClick={() => openModal("order", { itemName: item.name })}>
              Order online?
            </button>
          </div>
          {item.desc && <p className="ro-lead">{item.desc}</p>}
          <ul className="ro-five">
            <li>
              <strong>{item.category}</strong>
              <br />
              category
            </li>
            <li>
              <strong>{item.price}</strong>
              <br />
              listed price
            </li>
            {item.withText && (
              <li>
                <strong>with {item.withText}</strong>
                <br />
                served with
              </li>
            )}
            <li>
              <VegMark veg={item.veg} />
            </li>
          </ul>
          <div className="ro-switcher ro-switcher--items" aria-label={`Other ${category.name.toLowerCase()}`}>
            {siblings.map((s) => (
              <Link key={s.id} to={`/menu/item/${s.id}`} className={`ro-switcher__item ${s.id === item.id ? "is-current" : ""}`} aria-current={s.id === item.id ? "page" : undefined} aria-label={s.name}>
                <span className="ro-switcher__photo">{s.photo ? <img src={s.photo} alt="" /> : <CategoryIcon name={category.icon} />}</span>
                <span className="ro-switcher__caption">{s.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="ro-detail-info">
        <div className="ro-container">
          <details className="ro-disclosure">
            <summary>
              <span className="ro-disclosure__show">Show dish details</span>
              <span className="ro-disclosure__hide">Hide dish details</span>
            </summary>
            <div className="ro-information">
              <h2>Ingredients &amp; allergens</h2>
              <p>
                Ask the café about ingredients, allergens and cross-contact before ordering.
              </p>
              <h2>Dish information</h2>
              <table className="ro-info-table">
                <tbody>
                  <tr>
                    <td>Price</td>
                    <td>{item.price}</td>
                  </tr>
                  {item.withText && (
                    <tr>
                      <td>Served with</td>
                      <td>{item.withText}</td>
                    </tr>
                  )}
                  <tr>
                    <td>Category</td>
                    <td>{item.category}</td>
                  </tr>
                  <tr>
                    <td>Diet mark</td>
                    <td>{item.veg ? "Vegetarian" : "Non-vegetarian"}</td>
                  </tr>
                </tbody>
              </table>
              <Link className="ro-pill ro-pill--solid" to={`/menu/c/${category.slug}`}>
                Back to {category.name}
              </Link>
            </div>
          </details>
        </div>
      </section>

      <section className="ro-section ro-tinted ro-section--compact">
        <div className="ro-container ro-faq-wrap">
          <FaqList items={[faqs[2], faqs[5], faqs[6]]} numbered={false} />
        </div>
      </section>

      <section className="ro-section ro-related">
        <div className="ro-container">
          <h2>
            Featured <strong className="ro-italic">Pairings</strong>
          </h2>
          <div className="ro-related__grid">
            {related.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
          <Link className="ro-pill ro-pill--solid" to="/pairings">
            See all our pairings
          </Link>
        </div>
      </section>
      <FloatingWhatsApp phoneNumber={business.phone.whatsapp} statusMessage="Typically replies within 1 hour" accountName="Rollover Warangal" avatar={waAvatar} allowClickAway styles={{ zIndex: 60 }} />
    </article>
  );
}
